import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Tooltip,
  Skeleton,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import ArchivedProjects from "./ArchivedProjects";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  UITableShell,
  UITableToolbar,
  UIStatusChip,
  UITableActionCell,
} from "../../../../components/ui";
import { resolveCustomerReview } from "../../../../services/customerReview";

const TAB_OPTIONS = [
  { key: "client-hold", label: "Customers On Hold" },
  { key: "in-review", label: "Lead To Customer Review" },
  { key: "terms-review", label: "Terms Review" },
  { key: "suppliers", label: "Supplier Review" },
  { key: "archived-projects", label: "Archived Projects" },
];

const densitySettings = {
  compact: { py: 0.75, fontSize: "0.8125rem" },
  comfortable: { py: 1.5, fontSize: "0.875rem" },
  spacious: { py: 2.25, fontSize: "0.9375rem" },
};

const DASH = "—";

const getStatusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "approved") return "success";
  if (normalized === "declined") return "error";
  if (normalized === "rejected") return "error";
  if (normalized === "cancelled" || normalized === "canceled") return "neutral";
  if (normalized === "stale") return "warning";
  if (normalized === "in-review") return "warning";
  return "neutral";
};

const formatFallbackValue = (value, fallback = DASH) =>
  value === null || value === undefined || value === "" ? fallback : value;

export const shapeTermsReviewRow = (row = {}) => {
  const currentTerm =
    row.scope === "CUSTOMER_AND_PROJECT"
      ? [
          row.previousCustomerTerm?.title
            ? `Customer: ${row.previousCustomerTerm.title}`
            : null,
          row.previousProjectTerm?.title
            ? `Project: ${row.previousProjectTerm.title}`
            : row.previousResolvedProjectTerm?.title
              ? `Project: ${row.previousResolvedProjectTerm.title}`
              : null,
        ]
          .filter(Boolean)
          .join(" • ")
      : row.scope === "PROJECT_ONLY"
        ? row.previousProjectTerm?.title ||
          row.previousResolvedProjectTerm?.title ||
          DASH
        : row.previousCustomerTerm?.title || DASH;

  return {
    id: row.id,
    customerId: row.customer?.id || null,
    businessName: formatFallbackValue(row.customer?.businessName),
    projectName: row.order?.projectName || "Not applicable",
    scopeLabel: row.scopeLabel || row.scope || DASH,
    status: row.status || DASH,
    requestedTerm: row.requestedTerm?.title || row.requestedTermId || DASH,
    currentTerm,
    routingMode: row.routingMode || "TEAM_REVIEWER",
    routingLabel:
      row.routingMode === "FALLBACK_EMAIL" ? "Fallback Email" : "Team Reviewer",
    routingRecipient:
      row.routingMode === "FALLBACK_EMAIL"
        ? row.fallbackEmail || DASH
        : row.reviewerUser?.name ||
          (row.reviewerUserId ? `User #${row.reviewerUserId}` : "Configured Team Reviewer"),
    routingWarning: row.routingWarning || "",
    reviewComment: row.reviewerNote || row.reason || DASH,
    requestedBy: row.requestedByUser?.name || DASH,
    requestedAt: row.requestedAt || row.createdAt || null,
    raw: row,
  };
};

export const buildReviewRequestConfig = ({
  tab,
  api,
  rowId,
  action,
  comment = "",
}) => {
  const normalizedAction = String(action || "").trim().toLowerCase();

  if (tab === "suppliers") {
    return {
      method: "put",
      url: `${api}/suppliers/${rowId}/review`,
      body: { action: normalizedAction, comment },
    };
  }

  if (tab === "terms-review") {
    return {
      method: "post",
      url: `${api}/operations-management/term-requests/${rowId}/review`,
      body: {
        action: normalizedAction === "decline" ? "reject" : normalizedAction,
        reviewerNote: comment,
      },
    };
  }

  return {
    method: "put",
    url: `${api}/customers/${rowId}/review`,
    body: { action: normalizedAction, comment },
  };
};

export const isTermsReviewRejectionBlocked = ({ tab, action, comment = "" }) =>
  tab === "terms-review" &&
  String(action || "").trim().toLowerCase() === "decline" &&
  !String(comment || "").trim();

export const mergeReviewedRow = ({
  tab,
  existingRow,
  responseData,
  fallbackComment = "",
}) => {
  if (tab === "terms-review") {
    return shapeTermsReviewRow({
      ...(responseData?.request || existingRow?.raw || {}),
      reviewerNote:
        responseData?.request?.reviewerNote ??
        fallbackComment ??
        existingRow?.raw?.reviewerNote,
      reason:
        responseData?.request?.reason ??
        existingRow?.raw?.reason ??
        existingRow?.reviewComment,
    });
  }

  return {
    ...existingRow,
    status: responseData?.status || existingRow?.status,
    reviewComment: fallbackComment || existingRow?.reviewComment || DASH,
  };
};

export const mergeStaleTermsReviewRow = ({ existingRow, reason = "" }) => ({
  ...existingRow,
  status: "STALE",
  reviewComment: reason || existingRow?.reviewComment || DASH,
  raw: {
    ...(existingRow?.raw || {}),
    status: "STALE",
    reviewerNote: reason || existingRow?.raw?.reviewerNote || null,
  },
});

const formatTermReviewDate = (value) => {
  if (!value) return DASH;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? DASH : date.toLocaleDateString();
};

export default function InstanceOperations() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const isProjectArchiveAdmin = ["administrator", "super-administrator"].includes(String(authState?.role || authState?.user?.role || "").trim().toLowerCase());
  const [tab, setTab] = useState("in-review");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [density, setDensity] = useState("comfortable");
  const [densityMenuAnchor, setDensityMenuAnchor] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const api = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = useMemo(
    () => ({ accessToken: localStorage.getItem("accessToken") }),
    []
  );

  const currentDensity = densitySettings[density];
  const isSuppliers = tab === "suppliers";
  const isTermsReview = tab === "terms-review";
  const gridTitle = isSuppliers ? "Supplier" : "Customer";

  const lastSupplierNote = (notes) => {
    const arr = Array.isArray(notes)
      ? notes.filter((note) => !note?.systemReminder)
      : [];
    if (!arr.length) return "";
    const note = arr[arr.length - 1];
    return (note && note.message) || "";
  };

  const fetchRows = useCallback(
    async (request = { canceled: false }) => {
      setLoading(true);
      try {
        if (tab === "archived-projects") {
          if (!request.canceled) setRows([]);
        } else if (tab === "suppliers") {
          const res = await axios.get(`${api}/suppliers/review-queue`, {
            headers,
            params: { instanceUniqueId },
          });
          const suppliers = (res.data.rows || []).map((supplier) => ({
            id: supplier.Id ?? supplier.id,
            businessName: supplier.Name ?? supplier.name,
            status: supplier.Status ?? supplier.status,
            reviewComment:
              lastSupplierNote(
                supplier.SupplierNotes ?? supplier.supplierNotes
              ) || DASH,
            terms: "",
          }));
          if (!request.canceled) setRows(suppliers);
        } else if (tab === "terms-review") {
          const res = await axios.get(`${api}/operations-management/term-requests`, {
            headers,
            params: { instanceUniqueId, status: "ALL" },
          });
          const requests = (res.data.rows || []).map((row) =>
            shapeTermsReviewRow(row)
          );
          if (!request.canceled) setRows(requests);
        } else {
          const res = await axios.get(`${api}/customers/get-customers`, {
            headers,
            params: {
              instanceUniqueId,
              includeCurrentReview: true,
              ...(tab === "client-hold"
                ? { tab: "all", creditHoldOnly: true }
                : {}),
            },
          });
          const customers = (res.data.customers || []).map((customer) => ({
            id: customer.id,
            businessName: customer.businessName,
            status: customer.status,
            reviewComment: customer.currentReview?.comment || DASH,
            terms: customer.terms || "",
            termsName: customer.termsName || "",
            termsIsCreditHold: Boolean(customer.termsIsCreditHold),
            isCreditHold: Boolean(customer.isCreditHold),
          }));
          if (!request.canceled) setRows(customers);
        }
      } catch (error) {
        console.error("Operations load error:", error);
        if (!request.canceled) setRows([]);
      } finally {
        if (!request.canceled) setLoading(false);
      }
    },
    [api, headers, instanceUniqueId, tab]
  );

  useEffect(() => {
    const controller = { canceled: false };
    fetchRows(controller);
    return () => {
      controller.canceled = true;
    };
  }, [fetchRows]);

  const openReview = (row) => {
    setActiveRow(row);
    setReviewComment("");
    setReviewError("");
    setReviewOpen(true);
  };

  const closeReview = () => {
    if (submitting) return;
    setReviewOpen(false);
    setActiveRow(null);
    setReviewComment("");
    setReviewError("");
  };

  const handleReview = async (action) => {
    if (!activeRow) return;

    const comment = reviewComment.trim();
    setReviewError("");

    if (isTermsReviewRejectionBlocked({ tab, action, comment })) {
      setReviewError("A reviewer note is required to reject a Terms Review request.");
      return;
    }

    const requestConfig = buildReviewRequestConfig({
      tab,
      api,
      rowId: activeRow.id,
      action,
      comment,
    });

    try {
      setSubmitting(true);
      const res = tab === "in-review"
        ? await resolveCustomerReview({
            customerId: activeRow.id,
            action,
            comment,
          })
        : requestConfig.method === "post"
          ? await axios.post(requestConfig.url, requestConfig.body, { headers })
          : await axios.put(requestConfig.url, requestConfig.body, { headers });

      setRows((list) =>
        list.map((row) =>
          row.id === activeRow.id
            ? mergeReviewedRow({
                tab,
                existingRow: row,
                responseData: res.data,
                fallbackComment: comment,
              })
            : row
        )
      );

      closeReview();
    } catch (error) {
      console.error("Review failed", error);
      const staleReason =
        error?.response?.data?.error || error?.response?.data?.message;
      const staleStatus = String(
        error?.response?.data?.status || ""
      ).toUpperCase();

      if (
        tab === "terms-review" &&
        error?.response?.status === 409 &&
        staleStatus === "STALE"
      ) {
        const staleRow = mergeStaleTermsReviewRow({
          existingRow: activeRow,
          reason: staleReason,
        });
        setRows((list) =>
          list.map((row) => (row.id === activeRow.id ? staleRow : row))
        );
        setActiveRow(staleRow);
        setReviewError(
          staleReason || "This request became stale and can no longer be approved."
        );
      } else {
        setReviewError(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to submit review."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const displayed = useMemo(
    () =>
      rows.filter((row) => {
        if (tab === "in-review") return row.status === "in-review";
        if (tab === "client-hold") {
          return (
            row.isCreditHold === true ||
            row.termsIsCreditHold === true ||
            row.terms === "Credit Hold"
          );
        }
        return true;
      }),
    [rows, tab]
  );

  useEffect(() => {
    setPage(0);
  }, [tab, displayed.length]);

  const columns = useMemo(
    () =>
      isTermsReview
        ? [
            { id: "businessName", label: "Customer", minWidth: 220 },
            { id: "projectName", label: "Project", minWidth: 220 },
            { id: "scopeLabel", label: "Scope", minWidth: 160 },
            {
              id: "currentTerm",
              label: "Previous / Current Terms",
              minWidth: 260,
            },
            { id: "requestedTerm", label: "Requested Terms", minWidth: 180 },
            { id: "requestedBy", label: "Requester", minWidth: 180 },
            { id: "requestedAt", label: "Request Date", minWidth: 160 },
            { id: "status", label: "Status", minWidth: 130 },
            { id: "routing", label: "Routing", minWidth: 220 },
            {
              id: "reviewComment",
              label: "Reviewer Note / Reason",
              minWidth: 320,
            },
            { id: "actions", label: "Review Action", minWidth: 150, align: "center" },
          ]
        : [
            { id: "id", label: "ID", minWidth: 80 },
            { id: "businessName", label: gridTitle, minWidth: 260 },
            { id: "status", label: "Status", minWidth: 150 },
            { id: "reviewComment", label: "Last Comment", minWidth: 360 },
            { id: "actions", label: "Actions", minWidth: 150, align: "center" },
          ],
    [gridTitle, isTermsReview]
  );

  const pagedRows = useMemo(
    () => displayed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [displayed, page, rowsPerPage]
  );

  const emptyMessage =
    tab === "suppliers"
      ? "No suppliers are currently in review."
      : tab === "in-review"
        ? "No customers are currently in review."
        : tab === "terms-review"
          ? "No terms review requests are currently pending or awaiting follow-up."
          : "No customers on credit hold.";

  const firstRecord = displayed.length ? page * rowsPerPage + 1 : 0;
  const lastRecord = Math.min(page * rowsPerPage + rowsPerPage, displayed.length);

  if (tab === "archived-projects") {
    return (
      <div className="instance-dashboard">
        <InstanceLeftSidebar />
        <div className="rightSide">
          <ArchivedProjects instanceUniqueId={instanceUniqueId} onBack={() => setTab("in-review")} />
        </div>
      </div>
    );
  }

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />

      <div className="rightSide">
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 60px)",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <UITableShell
            customLayout
            paperSx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "3px solid",
              borderTopColor: "primary.main",
              minHeight: 0,
              boxShadow:
                "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
            }}
          >
            <UITableToolbar sx={{ backgroundColor: "background.paper" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1 }}>
                    Operations
                  </Typography>

                  <Chip
                    label={`${displayed.length} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {TAB_OPTIONS.filter(({ key }) => key !== "archived-projects" || isProjectArchiveAdmin).map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        onClick={() => setTab(key)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 26,
                          backgroundColor:
                            tab === key
                              ? "var(--ec-color-primary)"
                              : "var(--ec-status-neutral-bg)",
                          color:
                            tab === key
                              ? "var(--ec-color-surface)"
                              : "var(--ec-status-neutral-text)",
                          border:
                            tab === key
                              ? "1px solid transparent"
                              : "1px solid var(--ec-status-neutral-border)",
                          transition: "all 0.15s ease",
                          "& .MuiChip-label": { px: "10px" },
                          "&:hover": {
                            backgroundColor:
                              tab === key
                                ? "var(--ec-color-primary-dark)"
                                : "var(--ec-color-surface-muted)",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Row density">
                    <IconButton
                      size="small"
                      onClick={(event) => setDensityMenuAnchor(event.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <SettingsIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={densityMenuAnchor}
                    open={Boolean(densityMenuAnchor)}
                    onClose={() => setDensityMenuAnchor(null)}
                    slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2,
                        pt: 1.25,
                        pb: 0.5,
                        display: "block",
                        color: "text.disabled",
                      }}
                    >
                      Row density
                    </Typography>
                    {Object.keys(densitySettings).map((value) => (
                      <MenuItem
                        key={value}
                        onClick={() => {
                          setDensity(value);
                          setDensityMenuAnchor(null);
                        }}
                        sx={{ py: 0.75, textTransform: "capitalize" }}
                      >
                        <Checkbox
                          size="small"
                          checked={density === value}
                          sx={{
                            p: 0.5,
                            mr: 0.5,
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {value}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>

                  <Tooltip title="Refresh list">
                    <IconButton
                      size="small"
                      onClick={() => fetchRows({ canceled: false })}
                      disabled={loading}
                      sx={{ color: "text.secondary" }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </UITableToolbar>

            <TableContainer
              sx={{
                flex: 1,
                overflow: "auto",
                minHeight: 0,
                borderRadius: "0 !important",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--ec-color-border-strong)",
                  borderRadius: 99,
                },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 1000, width: "100%" }}>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align || "left"}
                        sx={{
                          minWidth: col.minWidth,
                          px: 2,
                          py: 1.25,
                          backgroundColor: "#f8fafc !important",
                          color: "var(--ec-table-head-text-muted)",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          borderBottom: "none",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent:
                              col.align === "center" ? "center" : "flex-start",
                          }}
                        >
                          {col.label}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                    "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                      borderBottom: "none",
                    },
                  }}
                >
                  {loading &&
                    [...Array(6)].map((_, index) => (
                      <TableRow key={index}>
                        {columns.map((col) => (
                          <TableCell key={col.id} sx={{ px: 2, py: currentDensity.py }}>
                            <Skeleton
                              variant="rectangular"
                              height={20}
                              sx={{ borderRadius: 1, opacity: 1 - index * 0.1 }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                  {!loading && displayed.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{ py: 8, borderBottom: "none", background: "transparent" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              backgroundColor: "var(--ec-color-surface-muted)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FolderOffIcon
                              sx={{ fontSize: 32, color: "var(--ec-color-text-muted)" }}
                            />
                          </Box>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              color="text.secondary"
                              gutterBottom
                            >
                              All caught up!
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ maxWidth: 340 }}
                            >
                              {emptyMessage}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    pagedRows.map((row) => {
                      const reviewable = isTermsReview
                        ? String(row.status || "").toUpperCase() === "PENDING"
                        : (tab === "in-review" || tab === "suppliers") &&
                          row.status === "in-review";

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            backgroundColor: "background.paper",
                            transition: "background-color 0.1s",
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          {!isTermsReview ? (
                            <>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.id ?? DASH}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                  fontWeight: 600,
                                }}
                              >
                                {row.businessName || DASH}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.status ? (
                                  <UIStatusChip
                                    label={row.status}
                                    tone={getStatusTone(row.status)}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    {DASH}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                <Stack spacing={0.5}>
                                  <Typography variant="body2" sx={{ fontSize: currentDensity.fontSize }}>
                                    {row.routingLabel || DASH}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ wordBreak: "break-word" }}
                                  >
                                    {row.routingRecipient || DASH}
                                  </Typography>
                                  {row.routingMode === "FALLBACK_EMAIL" ? (
                                    <Chip
                                      label="Unassigned Team Route"
                                      size="small"
                                      color="warning"
                                      sx={{ width: "fit-content", fontWeight: 600 }}
                                    />
                                  ) : null}
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                  maxWidth: 420,
                                }}
                              >
                                <Tooltip
                                  title={
                                    row.reviewComment && row.reviewComment !== DASH
                                      ? row.reviewComment
                                      : ""
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: currentDensity.fontSize,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color:
                                        row.reviewComment && row.reviewComment !== DASH
                                          ? "text.primary"
                                          : "text.disabled",
                                    }}
                                  >
                                    {row.reviewComment || DASH}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                  fontWeight: 600,
                                }}
                              >
                                {row.businessName || DASH}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.projectName || "Not applicable"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.scopeLabel || DASH}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                  maxWidth: 260,
                                }}
                              >
                                <Tooltip title={row.currentTerm || ""} placement="top" arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: currentDensity.fontSize }}
                                  >
                                    {row.currentTerm || DASH}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.requestedTerm || DASH}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.requestedBy || DASH}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {formatTermReviewDate(row.requestedAt)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                }}
                              >
                                {row.status ? (
                                  <UIStatusChip
                                    label={row.status}
                                    tone={getStatusTone(row.status)}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    {DASH}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 2,
                                  py: currentDensity.py,
                                  fontSize: currentDensity.fontSize,
                                  maxWidth: 320,
                                }}
                              >
                                <Tooltip
                                  title={
                                    row.reviewComment && row.reviewComment !== DASH
                                      ? row.reviewComment
                                      : ""
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: currentDensity.fontSize,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color:
                                        row.reviewComment && row.reviewComment !== DASH
                                          ? "text.primary"
                                          : "text.disabled",
                                    }}
                                  >
                                    {row.reviewComment || DASH}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </>
                          )}

                          <TableCell
                            align="center"
                            sx={{
                              px: 2,
                              py: currentDensity.py,
                              fontSize: currentDensity.fontSize,
                            }}
                          >
                            <UITableActionCell>
                              {reviewable ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openReview(row)}
                                  sx={{ textTransform: "none", fontWeight: 800 }}
                                >
                                  Review
                                </Button>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {DASH}
                                </Typography>
                              )}
                            </UITableActionCell>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                px: 2,
                py: 0,
                backgroundColor: "background.paper",
                borderTop: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 52,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "var(--ec-color-text-muted)",
                  fontSize: "0.8125rem",
                  pl: 1,
                }}
              >
                {displayed.length > 0
                  ? `Showing ${firstRecord}–${lastRecord} of ${displayed.length} records`
                  : loading
                    ? ""
                    : "No results"}
              </Typography>

              <TablePagination
                component="div"
                count={displayed.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  border: 0,
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 52,
                    px: 0,
                    alignItems: "center",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: "0.8125rem",
                      color: "var(--ec-color-text-muted) !important",
                      mb: 0,
                      lineHeight: 1,
                    },
                  "& .MuiTablePagination-select": { fontSize: "0.8125rem" },
                }}
              />
            </Box>

            <Dialog open={reviewOpen} onClose={closeReview} fullWidth maxWidth="sm">
              <DialogTitle sx={{ pb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {isTermsReview ? "Review Terms Request" : `Review ${gridTitle}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activeRow?.businessName || DASH} • ID #{activeRow?.id ?? DASH}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={
                      tab === "suppliers"
                        ? "Supplier Review"
                        : isTermsReview
                          ? "Terms Review"
                          : "Lead → Customer Review"
                    }
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </DialogTitle>

              <DialogContent sx={{ pt: 1 }}>
                <Divider sx={{ mb: 2 }} />

                {reviewError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {reviewError}
                  </Alert>
                ) : null}

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    {isTermsReview ? "Existing note or request reason" : "Last comment"}
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 2,
                      p: 1.5,
                      background: "#fafafa",
                    }}
                  >
                    <Typography variant="body2">
                      {activeRow?.reviewComment && activeRow.reviewComment !== DASH
                        ? activeRow.reviewComment
                        : isTermsReview
                          ? "No reviewer note or request reason."
                          : "No previous comment."}
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder={
                    isTermsReview
                      ? "Add a reviewer note. Rejecting a terms request requires one."
                      : "Add a comment for audit trail (recommended)…"
                  }
                  value={reviewComment}
                  onChange={(event) => setReviewComment(event.target.value)}
                  disabled={submitting}
                  error={
                    tab === "terms-review" &&
                    !reviewComment.trim() &&
                    reviewError.includes("required")
                  }
                  helperText={
                    isTermsReview
                      ? "Reviewer notes are saved on the terms request. Rejection requires a note."
                      : "This comment will be saved as the latest review note."
                  }
                />
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={closeReview} disabled={submitting}>
                  Cancel
                </Button>

                <Box sx={{ flex: 1 }} />

                <Button
                  variant="outlined"
                  onClick={() => handleReview("decline")}
                  disabled={
                    submitting ||
                    isTermsReviewRejectionBlocked({
                      tab,
                      action: "decline",
                      comment: reviewComment,
                    })
                  }
                  sx={{ fontWeight: 800 }}
                >
                  {submitting ? "Working…" : "Decline"}
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleReview("approve")}
                  disabled={submitting}
                  sx={{ fontWeight: 800 }}
                >
                  {submitting ? "Working…" : "Approve"}
                </Button>
              </DialogActions>
            </Dialog>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
}
